import styled, { createGlobalStyle, css, keyframes } from 'styled-components';

import { Text } from '../../../../components/Text/Text';
import {
  COLORS,
  FIXED_HEADER,
  LUMIO,
  LUMIO_CHAT,
  LUMIO_MOBILE,
  LUMIO_MOBILE_ANIM,
  SPACE,
} from '../../../../theme/tokens';
import { fadeMaskY, thinScrollbar } from '../../../../theme/mixins';
import { headerActionDisc, headerCloseDisc } from '../LumioChat/LumioChat.styles';

/* The footer pill mirrors the nav pill's footprint so the two read as ONE element
 * morphing into each other: same side inset (12px) and the same lift off the
 * bottom (20px + safe area) as MobileNav's PageShadow. */
const PILL_SIDE_INSET = 12;
const PILL_BOTTOM_LIFT = 20;
/* Fade-mask dissolve length just inside the header/footer (matches the desktop
 * Thread's THREAD_FADE so the chat dissolves identically). */
const MID_FADE = 24;
/* Resting gap between the last message and the pill when scrolled to the end. */
const MID_END_GAP = 28;

/* ── Open/close orchestration ─────────────────────────────────────────────── */

/** Locks the document scroll while the overlay is up (mobile makes the body the
 * scroller — see MainLayout.styles MobileBodyScroll — so we pin it here). */
export const ScrollLock = createGlobalStyle`
  ${FIXED_HEADER} {
    html,
    body {
      overflow: hidden !important;
      touch-action: none;
    }
  }
`;

/** Full-screen host. Transparent itself — the dark wash lives on `Backdrop` so it
 * can BLOOM in around the anchored pill (the page stays briefly visible behind the
 * fading wash → the "everything dissolves into dark" reveal). Kept in the tree
 * while on mobile (so the conversation survives a close/re-open) and hidden with
 * display:none when fully closed. */
export const Overlay = styled.div<{ $visible: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 60;
  display: ${(p) => (p.$visible ? 'block' : 'none')};
  overflow: hidden;
  color: ${LUMIO_MOBILE.greeting};
`;

/** The dark wash + glow field. Blooms (opacity 0→1) around the anchored pill so the
 * page appears to dissolve into the lumio space. */
export const Backdrop = styled.div<{ $entered: boolean }>`
  position: absolute;
  inset: 0;
  z-index: 0;
  background: ${LUMIO_MOBILE.backdrop};
  opacity: ${(p) => (p.$entered ? 1 : 0)};
  transition: opacity ${LUMIO_MOBILE_ANIM.backdropMs}ms ${LUMIO_MOBILE_ANIM.FADE_EASE};

  @media (prefers-reduced-motion: reduce) {
    transition: opacity ${LUMIO_MOBILE_ANIM.backdropMs}ms linear;
  }
`;

/* ── Glow waves ───────────────────────────────────────────────────────────── */

/* The wave is a TILED data-URI SVG (one seamless period) drifted via
 * `background-position` on a normal, non-GPU-promoted element. It is deliberately NOT
 * a 400%-wide transform layer: that needs a huge composited texture which Safari
 * culls (the topmost layer blinks out / "dropped frames"). background-position is a
 * paint, not a composited layer, so there is nothing to drop. The drift scrolls by
 * exactly one tile so the repeated background loops with no seam (see waves.ts). */
const TILE_PX = 460;
const waveScroll = keyframes`
  from { background-position-x: 0; }
  to { background-position-x: -${TILE_PX}px; }
`;
const waveScrollReverse = keyframes`
  from { background-position-x: -${TILE_PX}px; }
  to { background-position-x: 0; }
`;

/** A band that clips the glow layers (top or bottom). Screen-blends so the bright
 * waves add light over the near-black base; the bloom-in is owned by `Backdrop`. */
export const GlowBand = styled.div<{ $edge: 'top' | 'bottom'; $h: number; $opacity: number }>`
  position: absolute;
  left: 0;
  right: 0;
  ${(p) => (p.$edge === 'top' ? 'top: 0;' : 'bottom: 0;')}
  height: ${(p) => p.$h}px;
  overflow: hidden;
  pointer-events: none;
  mix-blend-mode: screen;
  opacity: ${(p) => p.$opacity};
  /* Clean blend group for the screen-blend (harmless; the Safari flicker fix lives
   * on GlowLayer — see the glow-waves header). */
  isolation: isolate;
`;

/** One scrolling wave: a tiling data-URI wave image drifted via background-position
 * (per-layer duration + direction + negative delay give parallax). `$posY` anchors
 * the glow to the band's edge (top band hugs the top, bottom band hugs the foot). */
export const GlowLayer = styled.div<{
  $img: string;
  $dur: number;
  $reverse?: boolean;
  $blur: string;
  $opacity: number;
  $delay?: number;
  $posY: 'top' | 'bottom';
}>`
  position: absolute;
  inset: 0;
  background-image: ${(p) => p.$img};
  background-repeat: repeat-x;
  background-position-x: 0;
  background-position-y: ${(p) => p.$posY};
  background-size: ${TILE_PX}px 100%;
  opacity: ${(p) => p.$opacity};
  filter: blur(${(p) => p.$blur});
  animation: ${(p) => (p.$reverse ? waveScrollReverse : waveScroll)} ${(p) => p.$dur}ms linear infinite;
  animation-delay: ${(p) => -(p.$delay ?? 0)}ms;

  @media (prefers-reduced-motion: reduce) {
    /* Hold a static frame — no perpetual motion. */
    animation: none;
    background-position-x: -${Math.round(TILE_PX * 0.37)}px;
  }
`;

/* ── Header ───────────────────────────────────────────────────────────────── */

/* The header band itself is just layout; its children animate in independently so
 * the dismiss control can appear with the bloom while the word-mark rises later.
 * The row wraps so the FTUX testers' strip (777ftuxqa, full width) sits on a line of
 * its own under it; the measured header height then pads the chat below. */
export const Header = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 3;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  padding: calc(env(safe-area-inset-top) + 14px) 18px 8px;
  pointer-events: none;

  & > * {
    pointer-events: auto;
  }
`;

/* The word-mark rises in as the final stage of the morph (after the orb lands). */
export const Logo = styled.div<{ $entered: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  opacity: ${(p) => (p.$entered ? 1 : 0)};
  transform: ${(p) => (p.$entered ? 'translateY(0)' : 'translateY(-10px)')};
  transition: opacity ${LUMIO_MOBILE_ANIM.contentMs}ms ${LUMIO_MOBILE_ANIM.FADE_EASE}
      ${(p) => (p.$entered ? LUMIO_MOBILE_ANIM.contentDelayMs : 0)}ms,
    transform ${LUMIO_MOBILE_ANIM.contentMs}ms ${LUMIO_MOBILE_ANIM.EASE}
      ${(p) => (p.$entered ? LUMIO_MOBILE_ANIM.contentDelayMs : 0)}ms;

  @media (prefers-reduced-motion: reduce) {
    transform: none;
    transition: opacity ${LUMIO_MOBILE_ANIM.contentMs}ms linear;
  }
`;

export const LogoMark = styled.span`
  display: block;
  width: 26px;
  height: 26px;

  svg,
  img {
    display: block;
    width: 100%;
    height: 100%;
  }
`;

export const LogoText = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 16px;
  line-height: 1.15;
  font-weight: 700;
  background: ${LUMIO.logoText};
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

/* Fades in with the backdrop bloom (no stagger delay) so a visible dismiss
 * affordance is present from the start of the open — the word-mark can rise later. */
export const CloseButton = styled.button<{ $entered: boolean }>`
  ${headerCloseDisc}
  opacity: ${(p) => (p.$entered ? 1 : 0)};
  transition: background 150ms ease, transform 150ms ease,
    opacity ${LUMIO_MOBILE_ANIM.backdropMs}ms ${LUMIO_MOBILE_ANIM.FADE_EASE};

  @media (prefers-reduced-motion: reduce) {
    transition: background 150ms ease, opacity ${LUMIO_MOBILE_ANIM.backdropMs}ms linear;
  }
`;

/* Logo + the action buttons, clustered on the left of the header (the close
 * control stays on the right via the header's space-between). */
export const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACE[2]};

  /* The logo has no transparent edge, but each button centres a 36px disc in a 48px
   * box (6px inset). Nudge the first button by that 6px so the logo->disc gap equals
   * the disc->disc gap between the two buttons. */
  & > button:first-of-type {
    margin-left: 6px;
  }
`;

/* New-conversation / past-conversations triggers. 48px touch target (the glyph is
 * smaller, centred) with the header-action ink, fading in with the backdrop bloom
 * like the close control. */
/** Header action buttons join the logo's entrance group: they begin rising in only
 * AFTER the logo settles (contentDelayMs + contentMs), then stagger per button. */
const actionRiseDelay = (entered: boolean, index: number): number =>
  entered ? LUMIO_MOBILE_ANIM.contentDelayMs + LUMIO_MOBILE_ANIM.contentMs + index * LUMIO_MOBILE_ANIM.actionStaggerMs : 0;

export const HeaderAction = styled.button<{ $entered: boolean; $index: number }>`
  ${headerActionDisc}
  /* Fade + slide down from the top, like the logo, sequenced after it. */
  opacity: ${(p) => (p.$entered ? 1 : 0)};
  transform: ${(p) => (p.$entered ? 'translateY(0)' : 'translateY(-10px)')};
  transition: opacity ${LUMIO_MOBILE_ANIM.contentMs}ms ${LUMIO_MOBILE_ANIM.FADE_EASE}
      ${(p) => actionRiseDelay(p.$entered, p.$index)}ms,
    transform ${LUMIO_MOBILE_ANIM.contentMs}ms ${LUMIO_MOBILE_ANIM.EASE}
      ${(p) => actionRiseDelay(p.$entered, p.$index)}ms;

  @media (prefers-reduced-motion: reduce) {
    transform: none;
    transition: opacity ${LUMIO_MOBILE_ANIM.contentMs}ms linear;
  }
`;

/* ── Chat middle (fade-masked scroller, identical mask to desktop) ─────────── */

export const Mid = styled.div<{ $headerH: number; $footerH: number; $welcome: boolean; $entered: boolean }>`
  position: absolute;
  inset: 0;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 14px;
  box-sizing: border-box;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  ${(p) => (p.$welcome ? 'justify-content: center;' : '')}
  padding: ${(p) => p.$headerH + MID_FADE}px 20px ${(p) => p.$footerH + MID_END_GAP}px;
  ${thinScrollbar}
  ${(p) => fadeMaskY(p.$headerH, p.$footerH, MID_FADE)}

  opacity: ${(p) => (p.$entered ? 1 : 0)};
  transition: opacity ${LUMIO_MOBILE_ANIM.contentMs}ms ${LUMIO_MOBILE_ANIM.FADE_EASE}
    ${(p) => (p.$entered ? LUMIO_MOBILE_ANIM.contentDelayMs : 0)}ms;
`;

/** Welcome block — greeting + suggestion shortcuts, vertically centred and gently
 * risen-in. Reuses the desktop Lumio welcome styled-components for the inner copy
 * and cards (imported in the component). */
export const Welcome = styled.div<{ $entered: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[24]};
  width: 100%;
  transform: ${(p) => (p.$entered ? 'translateY(0)' : 'translateY(12px)')};
  transition: transform ${LUMIO_MOBILE_ANIM.contentMs}ms ${LUMIO_MOBILE_ANIM.EASE}
    ${(p) => (p.$entered ? LUMIO_MOBILE_ANIM.contentDelayMs : 0)}ms;

  @media (prefers-reduced-motion: reduce) {
    transform: none;
    transition: none;
  }
`;

/* ── Footer: the morphing input pill + traveling orb ──────────────────────── */

/** Anchored at the exact nav-pill footprint so the bar reads as the morph anchor.
 * Visible from the first frame (does NOT fade with the backdrop) — the backdrop
 * blooms AROUND it while the orb travels inside it. */
export const Footer = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 3;
  padding: 0 ${PILL_SIDE_INSET}px calc(${PILL_BOTTOM_LIFT}px + env(safe-area-inset-bottom));
`;

/** The FTUX testers' strip over the input pill (empty for owners: the strip renders nothing). */
export const QaSlot = styled.div`
  &:not(:empty) {
    margin-bottom: ${SPACE[12]};
  }
`;

/** The pill is the morph ANCHOR — it stays put on the nav pill's exact footprint
 * while everything blooms around it. Its BACKGROUND lives on a `::before` that
 * crossfades in (so the nav pill's icons dissolve underneath it), while the real
 * children — the composer copy and the orb — sit above and stay crisp (the orb
 * must read solid as it slides, not fade with the surface). */
export const InputPill = styled.div<{ $entered: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  box-sizing: border-box;
  width: 100%;
  min-height: 64px;
  padding: ${SPACE[8]} ${SPACE[8]} ${SPACE[8]} 22px;
  border-radius: 999px;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 999px;
    background: ${LUMIO_MOBILE.pillBg};
    border: 1px solid ${LUMIO_MOBILE.pillBorder};
    box-shadow: ${LUMIO_MOBILE.pillShadow};
    opacity: ${(p) => (p.$entered ? 1 : 0)};
    transition: opacity ${LUMIO_MOBILE_ANIM.backdropMs}ms ${LUMIO_MOBILE_ANIM.FADE_EASE};
    pointer-events: none;
  }

  & > * {
    position: relative;
  }

  /* The composer drops the browser's outline, so the pill's border says where the typing goes. */
  &:focus-within::before {
    border-color: ${COLORS.accent};
  }
`;

export const Composer = styled.textarea<{ $entered: boolean }>`
  flex: 1 1 auto;
  min-width: 0;
  margin: 0;
  padding: 0;
  border: none;
  outline: none;
  resize: none;
  background: transparent;
  color: ${LUMIO_MOBILE.composerText};
  font-family: inherit;
  font-size: 16px;
  font-weight: 300;
  line-height: 22px;
  min-height: 22px;
  max-height: 104px;
  overflow-y: auto;
  scrollbar-width: thin;

  &::placeholder {
    color: ${LUMIO_MOBILE.placeholder};
  }

  /* The placeholder/typed copy eases in after the orb has travelled + swapped, so
   * the empty pill reads as the nav pill first, then becomes the composer. */
  opacity: ${(p) => (p.$entered ? 1 : 0)};
  transform: ${(p) => (p.$entered ? 'translateX(0)' : 'translateX(-8px)')};
  transition: opacity ${LUMIO_MOBILE_ANIM.contentMs}ms ${LUMIO_MOBILE_ANIM.FADE_EASE}
      ${(p) => (p.$entered ? LUMIO_MOBILE_ANIM.contentDelayMs : 0)}ms,
    transform ${LUMIO_MOBILE_ANIM.contentMs}ms ${LUMIO_MOBILE_ANIM.EASE}
      ${(p) => (p.$entered ? LUMIO_MOBILE_ANIM.contentDelayMs : 0)}ms;

  @media (prefers-reduced-motion: reduce) {
    transform: none;
    transition: opacity ${LUMIO_MOBILE_ANIM.contentMs}ms linear;
  }
`;

const orbShared = css`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  flex: 0 0 48px;
  padding: 0;
  border: none;
  border-radius: 50%;
`;

/** The traveling orb. `--orb-travel-x/y` is the measured resting→nav-orb offset
 * (px); on enter it animates from that offset to 0 (its resting send position), so
 * it appears to slide out from exactly where the nav's centre orb sits. */
export const Orb = styled.button<{
  $entered: boolean;
  $armed?: boolean;
  $handoff?: boolean;
  $disabled?: boolean;
}>`
  ${orbShared}
  background: ${LUMIO_MOBILE.orbBg};
  color: ${LUMIO_MOBILE.orbGlyph};
  backdrop-filter: blur(4px);
  cursor: ${(p) => (p.$disabled ? 'default' : 'pointer')};
  transform: ${(p) =>
    p.$entered
      ? 'translate3d(0px, 0px, 0)'
      : 'translate3d(var(--orb-travel-x, 0px), var(--orb-travel-y, 0px), 0)'};
  /* Fades out on the close hand-off (crossfading with the nav orb fading in). */
  opacity: ${(p) => (p.$handoff ? 0 : 1)};
  /* Armed = placed at the start with NO transition (one frame); otherwise it holds
   * at the nav-orb spot for a beat on open then slides to rest, slides straight
   * back on close (no delay), and crossfades out at the hand-off. */
  transition: ${(p) =>
    p.$armed
      ? 'none'
      : `transform ${LUMIO_MOBILE_ANIM.orbSlideMs}ms ${LUMIO_MOBILE_ANIM.EASE} ${
          p.$entered ? LUMIO_MOBILE_ANIM.orbSlideDelayMs : 0
        }ms, opacity ${LUMIO_MOBILE_ANIM.orbCrossfadeMs}ms ${LUMIO_MOBILE_ANIM.FADE_EASE}`};

  /* inset -4px matches the nav centre orb's ring (MobileNav CenterBtn::before) so
   * the rings are pixel-identical at the crossfade and the morph reads as one orb. */
  &::before {
    content: '';
    position: absolute;
    inset: -4px;
    border-radius: 50%;
    padding: ${SPACE[2]};
    background: ${LUMIO_MOBILE.orbRing};
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
  }

  &:active {
    transform: ${(p) =>
        p.$entered
          ? 'translate3d(0px, 0px, 0)'
          : 'translate3d(var(--orb-travel-x, 0px), var(--orb-travel-y, 0px), 0)'}
      scale(0.94);
  }

  @media (prefers-reduced-motion: reduce) {
    transform: none;
    transition: none;
    /* No crossfade — stay solid; the orb just cuts to the nav orb when the overlay
     * hides (the nav orb is already in place). */
    opacity: 1;
  }
`;

/** Stop control — same orb geometry, brand-gradient fill + working pulse. */
const orbPulse = keyframes`
  0% { box-shadow: 0 0 0 0 ${LUMIO.stopRing}; }
  70% { box-shadow: 0 0 0 8px transparent; }
  100% { box-shadow: 0 0 0 0 transparent; }
`;

export const StopOrb = styled.button`
  ${orbShared}
  background: ${LUMIO.stopBg};
  color: ${LUMIO.stopIcon};
  cursor: pointer;
  animation: ${orbPulse} ${LUMIO_CHAT.stopPulseMs}ms ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/** Crossfading glyph stack inside the orb (spark ⇄ send). Both layers are stacked;
 * opacity + a touch of scale swap them. `$delay` lets the swap LAG the orb's slide
 * so it reads as "slide into the send position, then the glyph swaps". */
export const Glyph = styled.span<{ $show: boolean; $delay?: number }>`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${(p) => (p.$show ? 1 : 0)};
  transform: ${(p) => (p.$show ? 'scale(1)' : 'scale(0.6)')};
  transition: opacity ${LUMIO_MOBILE_ANIM.glyphMs}ms ${LUMIO_MOBILE_ANIM.FADE_EASE} ${(p) => p.$delay ?? 0}ms,
    transform ${LUMIO_MOBILE_ANIM.glyphMs}ms ${LUMIO_MOBILE_ANIM.EASE} ${(p) => p.$delay ?? 0}ms;

  svg {
    display: block;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: opacity ${LUMIO_MOBILE_ANIM.glyphMs}ms linear;
    transform: none;
  }
`;
